import React, { useRef, useState, useEffect } from 'react';
import './Tours.css';

// Fonction pour charger dynamiquement les médias d'un dossier
const loadGalleryMedia = (folderName) => {
  try {
    // Charger images et vidéos du dossier
    const mediaContext = require.context(
      '../../../public/images/gallery',
      true,
      /\.(jpg|jpeg|png|gif|webp|mp4|webm|mov)$/
    );

    const mediaPaths = mediaContext.keys();

    // Filtrer les médias du dossier spécifique
    const folderMedia = mediaPaths
      .filter(path => path.startsWith(`./${folderName}/`))
      .map((path) => {
        const fileName = path.replace(`./${folderName}/`, '');
        const extension = fileName.split('.').pop().toLowerCase();
        const isVideo = ['mp4', 'webm', 'mov'].includes(extension);

        return {
          type: isVideo ? 'video' : 'image',
          src: `${process.env.PUBLIC_URL}/images/gallery/${folderName}/${fileName}`
        };
      });

    // Trier: images d'abord, puis vidéos
    return folderMedia.sort((a, b) => {
      if (a.type === 'image' && b.type === 'video') return -1;
      if (a.type === 'video' && b.type === 'image') return 1;
      return 0;
    });
  } catch (error) {
    console.error(`Error loading gallery for ${folderName}:`, error);
    return [];
  }
};

const Tours = ({ t }) => {
  const scrollRef = useRef(null);
  const [selectedTour, setSelectedTour] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [toursWithGallery, setToursWithGallery] = useState([]);

  // États pour la galerie: filtre et pagination
  const [galleryFilter, setGalleryFilter] = useState('all'); // 'all', 'image', 'video'
  const [galleryPage, setGalleryPage] = useState(0);
  const itemsPerPage = 6;

  // Configuration de base des tours (sans gallery, elle sera chargée dynamiquement)
  const toursConfig = [
    {
      title: t.list[0].title,
      description: t.list[0].description,
      duration: t.list[0].duration,
      groupSize: t.list[0].groupSize,
      location: t.list[0].location,
      coordinates: t.list[0].coordinates,
      image: `${process.env.PUBLIC_URL}/images/goree.jpg`,
      category: 'Cultural Discovery',
      galleryFolder: 'Goree'
    },
    {
      title: t.list[1].title,
      description: t.list[1].description,
      duration: t.list[1].duration,
      groupSize: t.list[1].groupSize,
      location: t.list[1].location,
      coordinates: t.list[1].coordinates,
      image: `${process.env.PUBLIC_URL}/images/lacrose.jpg`,
      category: 'Natural Wonders',
      galleryFolder: 'Lac Rose'
    },
    {
      title: t.list[2].title,
      description: t.list[2].description,
      duration: t.list[2].duration,
      groupSize: t.list[2].groupSize,
      location: t.list[2].location,
      coordinates: t.list[2].coordinates,
      image: `${process.env.PUBLIC_URL}/images/gallery/Sine Saloum/Screenshot_20240917-072830_WhatsApp.jpg`,
      category: 'Nature & Wildlife',
      galleryFolder: 'Sine Saloum'
    },
    {
      title: t.list[3].title,
      description: t.list[3].description,
      duration: t.list[3].duration,
      groupSize: t.list[3].groupSize,
      location: t.list[3].location,
      coordinates: t.list[3].coordinates,
      image: `${process.env.PUBLIC_URL}/images/20240504_091008.jpg`,
      category: 'Safari Experience',
      galleryFolder: 'Bandia'
    },
    {
      title: t.list[4].title,
      description: t.list[4].description,
      duration: t.list[4].duration,
      groupSize: t.list[4].groupSize,
      location: t.list[4].location,
      coordinates: t.list[4].coordinates,
      image: `${process.env.PUBLIC_URL}/images/saintlouis.jpg`,
      category: 'Cultural Discovery',
      galleryFolder: 'Saint-Louis'
    },
    {
      title: t.list[5].title,
      description: t.list[5].description,
      duration: t.list[5].duration,
      groupSize: t.list[5].groupSize,
      location: t.list[5].location,
      coordinates: t.list[5].coordinates,
      image: `${process.env.PUBLIC_URL}/images/gallery/Dakar city/20240527_090252.jpg`,
      category: 'City Tours',
      galleryFolder: 'Dakar city'
    },
    {
      title: t.list[6].title,
      description: t.list[6].description,
      duration: t.list[6].duration,
      groupSize: t.list[6].groupSize,
      location: t.list[6].location,
      coordinates: t.list[6].coordinates,
      image: `${process.env.PUBLIC_URL}/images/gallery/Pays Bassari/Screenshot_20251003-094633_Google.jpg`,
      category: 'Cultural Heritage',
      galleryFolder: 'Pays Bassari'
    },
    {
      title: t.list[7].title,
      description: t.list[7].description,
      duration: t.list[7].duration,
      groupSize: t.list[7].groupSize,
      location: t.list[7].location,
      coordinates: t.list[7].coordinates,
      image: `${process.env.PUBLIC_URL}/images/gallery/Casamance/20260205_103759.jpg`,
      category: 'Cultural & Nature',
      galleryFolder: 'Casamance'
    },
    {
      title: t.list[8].title,
      description: t.list[8].description,
      duration: t.list[8].duration,
      groupSize: t.list[8].groupSize,
      location: t.list[8].location,
      coordinates: t.list[8].coordinates,
      image: `${process.env.PUBLIC_URL}/images/lompoul.jpg`,
      category: 'Desert Adventure',
      galleryFolder: 'Lompoul'
    },
    {
      title: t.list[9].title,
      description: t.list[9].description,
      duration: t.list[9].duration,
      groupSize: t.list[9].groupSize,
      location: t.list[9].location,
      coordinates: t.list[9].coordinates,
      image: `${process.env.PUBLIC_URL}/images/gambia.jpg`,
      category: 'International Tour',
      galleryFolder: 'Gambia'
    }
  ];

  // Charger les galeries dynamiquement au montage du composant
  useEffect(() => {
    const loadAllGalleries = () => {
      const toursWithMedia = toursConfig.map(tour => ({
        ...tour,
        gallery: loadGalleryMedia(tour.galleryFolder)
      }));
      setToursWithGallery(toursWithMedia);
    };

    loadAllGalleries();
  }, [t]);

  // Utiliser les tours avec galeries chargées, ou la config de base en fallback
  const tours = toursWithGallery.length > 0 ? toursWithGallery : toursConfig;

  const scroll = (direction) => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = 350;
      if (direction === 'left') {
        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        });
      } else {
        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  const openModal = (tour) => {
    setSelectedTour(tour);
    setActiveTab('info');
    setGalleryFilter('all');
    setGalleryPage(0);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedTour(null);
    setActiveTab('info');
    setGalleryFilter('all');
    setGalleryPage(0);
    document.body.style.overflow = 'auto';
  };

  const navigateToNextTour = () => {
    const currentIndex = tours.findIndex(tour => tour.title === selectedTour.title);
    const nextIndex = (currentIndex + 1) % tours.length;
    setSelectedTour(tours[nextIndex]);
    setActiveTab('info');
    setGalleryFilter('all');
    setGalleryPage(0);
  };

  const navigateToPrevTour = () => {
    const currentIndex = tours.findIndex(tour => tour.title === selectedTour.title);
    const prevIndex = (currentIndex - 1 + tours.length) % tours.length;
    setSelectedTour(tours[prevIndex]);
    setActiveTab('info');
    setGalleryFilter('all');
    setGalleryPage(0);
  };

  return (
    <section id="tours" className="tours-section">
      <div className="tours-container">
        <div className="tours-header">
          <h2 className="tours-main-title">
            {t.title.toUpperCase()}
          </h2>
          <div className="slider-controls">
            <button onClick={() => scroll('left')} className="control-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
            <button onClick={() => scroll('right')} className="control-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="tours-tabs">
          <button className="tab-btn active">{t.thingsToDo}</button>
        </div>

        <div className="tours-slider">
          <div className="tours-track" ref={scrollRef}>
            {tours.map((tour, index) => (
              <div key={index} className="tour-slide" onClick={() => openModal(tour)}>
                <div className="tour-image-wrapper">
                  <img src={tour.image} alt={tour.title} />
                  <div className="tour-overlay">
                    <span className="tour-category">{tour.category}</span>
                    <h3 className="tour-title">{tour.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedTour && (
        <div className="tour-modal" onClick={closeModal}>
          <button className="modal-nav-prev" onClick={(e) => { e.stopPropagation(); navigateToPrevTour(); }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>

          <button className="modal-nav-next" onClick={(e) => { e.stopPropagation(); navigateToNextTour(); }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>

          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>

            <div className="modal-image">
              <img src={selectedTour.image} alt={selectedTour.title} />
              <div className="modal-image-overlay">
                <span className="modal-category">{selectedTour.category}</span>
              </div>
            </div>

            <div className="modal-body">
              <h2 className="modal-title">{selectedTour.title}</h2>

              <div className="modal-tabs">
                <button
                  className={`modal-tab ${activeTab === 'info' ? 'active' : ''}`}
                  onClick={() => setActiveTab('info')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Informations
                </button>
                <button
                  className={`modal-tab ${activeTab === 'location' ? 'active' : ''}`}
                  onClick={() => setActiveTab('location')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Localisation
                </button>
                <button
                  className={`modal-tab ${activeTab === 'gallery' ? 'active' : ''}`}
                  onClick={() => setActiveTab('gallery')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                    <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Galerie
                </button>
                <button
                  className={`modal-tab ${activeTab === 'additional' ? 'active' : ''}`}
                  onClick={() => setActiveTab('additional')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Informations Additionnelles
                </button>
              </div>

              <div className="modal-tab-content">
                {activeTab === 'info' && (
                  <div className="tab-pane">
                    <div className="modal-meta">
                      <div className="meta-item">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                          <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span>{selectedTour.duration}</span>
                      </div>
                      <div className="meta-item">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span>{selectedTour.groupSize}</span>
                      </div>
                      <div className="meta-item">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span>{selectedTour.location}</span>
                      </div>
                    </div>
                    <p className="modal-description">{selectedTour.description}</p>
                  </div>
                )}

                {activeTab === 'location' && (
                  <div className="tab-pane">
                    <h4>Localisation</h4>
                    <p className="location-info">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '8px'}}>
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      {selectedTour.location}
                    </p>
                    <div className="location-map">
                      <iframe
                        width="100%"
                        height="400"
                        style={{border: 0, borderRadius: '8px', marginTop: '20px'}}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps?q=${selectedTour.coordinates}&output=embed&zoom=13`}>
                      </iframe>
                    </div>
                  </div>
                )}

                {activeTab === 'gallery' && (
                  <div className="tab-pane">
                    {/* Filtres */}
                    <div className="gallery-filters">
                      <button
                        className={`filter-btn ${galleryFilter === 'all' ? 'active' : ''}`}
                        onClick={() => { setGalleryFilter('all'); setGalleryPage(0); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                          <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                          <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                          <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Tout ({selectedTour.gallery?.length || 0})
                      </button>
                      <button
                        className={`filter-btn ${galleryFilter === 'image' ? 'active' : ''}`}
                        onClick={() => { setGalleryFilter('image'); setGalleryPage(0); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                          <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Photos ({selectedTour.gallery?.filter(m => m.type === 'image').length || 0})
                      </button>
                      <button
                        className={`filter-btn ${galleryFilter === 'video' ? 'active' : ''}`}
                        onClick={() => { setGalleryFilter('video'); setGalleryPage(0); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <polygon points="5,3 19,12 5,21" stroke="currentColor" strokeWidth="2" fill="none"/>
                        </svg>
                        Vidéos ({selectedTour.gallery?.filter(m => m.type === 'video').length || 0})
                      </button>
                    </div>

                    {/* Grille de médias */}
                    <div className="tour-gallery-grid">
                      {(() => {
                        const filteredMedia = selectedTour.gallery?.filter(m =>
                          galleryFilter === 'all' ? true : m.type === galleryFilter
                        ) || [];
                        const totalPages = Math.ceil(filteredMedia.length / itemsPerPage);
                        const paginatedMedia = filteredMedia.slice(
                          galleryPage * itemsPerPage,
                          (galleryPage + 1) * itemsPerPage
                        );

                        return (
                          <>
                            {paginatedMedia.map((media, index) => (
                              <div key={index} className={`tour-gallery-item ${media.type === 'video' ? 'video-item' : ''}`}>
                                {media.type === 'image' ? (
                                  <img src={media.src} alt={`${selectedTour.title} ${index + 1}`} />
                                ) : (
                                  <div className="video-wrapper">
                                    <video
                                      controls
                                      preload="metadata"
                                      poster={selectedTour.image}
                                    >
                                      <source src={media.src} type="video/mp4" />
                                      Votre navigateur ne supporte pas la lecture de vidéos.
                                    </video>
                                    <div className="video-play-overlay">
                                      <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" fill="rgba(0,0,0,0.6)"/>
                                        <path d="M10 8l6 4-6 4V8z" fill="white"/>
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}

                            {/* Message si aucun média */}
                            {paginatedMedia.length === 0 && (
                              <div className="no-media-message">
                                <p>Aucun média disponible</p>
                              </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                              <div className="gallery-pagination">
                                <button
                                  className="pagination-btn"
                                  onClick={() => setGalleryPage(p => Math.max(0, p - 1))}
                                  disabled={galleryPage === 0}
                                >
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                </button>
                                <div className="pagination-info">
                                  {galleryPage + 1} / {totalPages}
                                </div>
                                <button
                                  className="pagination-btn"
                                  onClick={() => setGalleryPage(p => Math.min(totalPages - 1, p + 1))}
                                  disabled={galleryPage >= totalPages - 1}
                                >
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {activeTab === 'additional' && (
                  <div className="tab-pane">
                    <p className="modal-description">Informations additionnelles à venir...</p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button className="btn-book">{t.bookNow}</button>
                <button className="btn-contact">Contact Us</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Tours;
